/** Actions -
 * Asset Editor Business Logics
 * Used via $store.commit()
 * @returns {Object}
 */

import app from 'app';
import Vue from 'vue';
import Analytics from 'c/WtAnalytics';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import { RatioChangeFixHandler } from '@wochitnpm/adjustasset';
import { checkIfPreviewNeeded } from './assetEditorApi.js';
import storyViewBus from 'v/Story/storyViewBus';
const RCFH = new RatioChangeFixHandler();
import { workPool } from '@wochitnpm/helpers';
import { isAssetCandidateForTranscribe } from 'v/Story/Timeline/api/transcription';
import {
  buildTrackingPayload,
  getActiveTrackedFields,
  getPresetFields,
  getSegmentTimes,
  getTrackedAreaFields,
  getTrackedAssetRect,
  isSameTrackedArea,
  isTrackableAsset,
} from 'AssetEditor/functionality/CustomOverlays/objectTrackingApi';

export default {
  /**
   * remove listeners
   */
  DESTROY_MODULE({ state }) {
    state.assetModel.free();
  },

  /**
   * Resets an page attributes from AGD object
   * Finds page AGD attributes
   * & commits Store setAGD to Null for each attr
   * dispatches generateRTPByWorkModel for a new RTPMODEL
   */
  removePage({ dispatch, commit, state, getters }, pageId) {
    const page = _.find(state.pages, (p) => p.id === pageId);

    if (page) {
      const agd = getters.AGD || {};

      _.each(page.agdAttributes, (attr) => {
        const agdPath = attr.split('.');
        const setter = {};

        _.each(agdPath, (key, i) => {
          const next = agdPath[i + 1];
          const prev = agdPath[i - 1];

          if (next) {
            const curAgd = setter[key] || agd[key];
            setter[key] = _.isObject(curAgd)
              ? _.filter(curAgd, (p) => p.id !== next)
              : null;
          } else if (!prev) {
            setter[key] = null;
          }
        });

        commit('setAGD', setter);
      });

      if (!page.agdAttributes || page.agdAttributes.length === 0) {
        app.doctorController.report(
          `Asset Editor Store removePage -> ${pageId} doesn't contain any AGD attributes`
        );
      } else {
        dispatch('generateSTPByWorkModel');
        dispatch('generateSceneLayoutSTP');
        dispatch('generateRTPByWorkModel').then(function (response) {
          if (!response.rtpExists) {
            bus.$emit('updateStage');
          }
        });
        dispatch('updatePage', {
          pageId: page.id,
          pageChanges: {
            fields: _.map(page.fields, (p) => _.omit(p, 'value')),
          },
        });

        // WTF ahead: if you apply crop effect - page.id === 'zoom',
        // but if you remove crop effect from effect-list, then - page.id === 'crop'
        if (page.id === 'crop' && state.assetModel.isSOCIAL()) {
          workPool.job.cropSocialAsset.execute({
            asset: state.assetModel.attributes,
            programmer: app.rootStore.getters.storyProgrammer,
          });
        }
      }
    } else {
      app.doctorController.report(
        `Asset Editor Store removePage -> cannot find (${pageId})`
      );
    }
  },

  /**
   * Switch asset editor's main page
   * Looks for id inside pages
   * commits Store setCurrentPage
   */
  changeCurrentPage({ commit, state, dispatch }, pageId) {
    if (_.find(state.pages, (p) => p.id === pageId)) {
      commit('setCurrentPage', pageId);

      if (pageId !== 'preview') {
        commit('assignTmpAGD');
        commit('resetTmpPageState');
        commit('setShouldPreviewAutoplay', false);
        dispatch('generateSTPByWorkModel');
      } else {
        commit('assignSTP', {
          status: null,
          lastChecksum: null,
          src: null,
        });
      }
    } else {
      app.doctorController.report(
        `Asset Editor Store changeCurrentPage -> cannot find page (${pageId})`
      );
    }
  },

  /**
   * change asset editor's page
   * Looks for pageId inside pages
   * commits Store updatePage
   */
  updatePage({ commit, state }, { pageId, pageChanges }) {
    if (_.find(state.pages, (p) => p.id === pageId)) {
      commit('updatePage', { pageId, pageChanges });
    } else {
      app.doctorController.report(
        `Asset Editor Store updatePage -> cannot find (${pageId})`
      );
    }
  },

  /**
   * adds asset editor's pages
   * commits Store addPage
   */
  addPages({ state, commit }, newPages) {
    if (!_.isArray(newPages)) {
      const type = typeof newPages;
      app.doctorController.report(
        `Asset Editor Store addPages: newPages should be an array! got: ${type}`
      );
      return;
    }

    const pages = newPages.filter((p) => _.isObject(p) && p.id);
    state.pages = [...pages, ...state.pages];
  },

  generateSceneLayoutSTP({ state, commit, getters }) {
    return new Promise((resolve, reject) => {
      const visualTrack = getters.getVisualTrackSceneLayoutOnly;
      const stpPromise = app.realTimePreviewController.addTrackToPreviewQueue({
        delay: 0,
        visualTrack,
        silent: true,
        type: app.RealtimePreviewType.STATIC_MASK,
        source: 'assetEditor',
        forceStoryAspectRatio: true,
        height: 774,
      });

      stpPromise
        .then((stpModel) => {
          commit('assignSceneLayoutSTPModel', stpModel);
          resolve();
        })
        .fail((message) => {
          commit('assignSceneLayoutSTPModel', null);
          reject({
            message,
          });
        });
    });
  },

  /**
   * Creates an RTP Model
   * Aborts current rtpmodel progress
   * commits Store _tmpRealTimePreviewChecksum with new checksum
   * commits Store setRTPModel with new rtpModel
   */
  generateRTPByWorkModel({ commit, getters, state }) {
    return new Promise((resolve) => {
      if (state.rtpModel) {
        if (
          state.rtpModel.get('realTimePreviewChecksum') ===
          getters.calRTPChecksum
        ) {
          // current rtp model is already up to date, why bother to create a new one?
          resolve({ rtpExists: true });
          return;
        } else {
          // current rtpmodel is invalid
          app.realTimePreviewController.abortRTP(
            state.rtpModel.get('realTimePreviewChecksum')
          );
        }
      }

      const rtpPromise = app.realTimePreviewController.addTrackToPreviewQueue({
        delay: 0,
        visualTrack: state.workModel,
        silent: true,
        type: app.RealtimePreviewType.VIDEO_PARTS,
        source: 'assetEditor',
      });

      rtpPromise
        .then((rtpModel) => {
          commit('assignRTPModel', rtpModel);
          resolve({ rtpExists: true });
        })
        .fail(() => {
          commit('assignRTPModel', null);
          resolve({
            rtpExists: false,
            message: 'shouldPullPreview failed, no need for rtpModel',
          });
        });
    });
  },

  /**
   * Throw away the current preview and build a new one.
   *
   * generateRTPByWorkModel refuses to work when the checksum has not changed,
   * which is exactly the case after an object tracking job finishes: the agd is
   * untouched, only the data the renderer pulls has changed. Dropping the model
   * is enough to get past that guard - it only looks at `state.rtpModel`.
   *
   * Deliberately NOT app.resetVisualTrackRTP: that nulls
   * `realTimePreviewResultData` on the work model, which `isDirty` diffs against
   * the asset model, so a finished job would silently mark an untouched asset
   * dirty and closing the editor would commit an edit the user never made.
   */
  REGENERATE_RTP({ commit, dispatch }) {
    commit('assignRTPModel', null);

    return dispatch('generateRTPByWorkModel');
  },

  /**
   * Track analytics
   */
  track({ state }, { e, params }) {
    let prefix;
    prefix = state.analyticsData.eventPrefix;
    prefix = prefix || 'unknown';
    params = _.extend({}, state.analyticsData, params);
    delete params.eventPrefix;
    Analytics.trigger(prefix + e + '', params);
  },

  // turn on / off flag for navigating away, resolve if true
  allowToNavigateAway({ commit, state }, canLeave = true) {
    commit('setIsValidToLeave', canLeave);
    if (canLeave) {
      state.validToLeavePromise.resolve();
    } else {
      state.validToLeavePromise = new _.ColdPromise();
    }
  },

  // in asset navigation should leave promised
  navigationPrepareToLeave({ state }) {
    if (state.isValidToLeave) {
      state.validToLeavePromise.resolve();
    }
    return state.validToLeavePromise;
  },

  setOverlayAssetCustomOverlayValue({ getters }, { co, field }) {
    const { selectedOverlayAsset } = getters;
    if (!selectedOverlayAsset) {
      return;
    }
    const { agd } = selectedOverlayAsset;
    if (!agd) {
      return;
    }
    let shouldIgnore = false;
    const fieldFromThemeInfo = co.fields.find((f) => f.name === field.name);
    if (field.type === 'DROPDOWN') {
      shouldIgnore =
        field.value === fieldFromThemeInfo?.optionThumbnails?.[0].id;
    }
    if (!agd.customOverlays) {
      agd.customOverlays = [];
    }
    const preset = agd.customOverlays.find((p) => p.id === co.id);
    if (preset) {
      if (shouldIgnore) {
        const currentFieldIndex = preset.fields.findIndex(
          (f) => f.name === field.name
        );
        if (currentFieldIndex > -1) {
          preset.fields.splice(currentFieldIndex, 1);
        }
        if (!preset.fields.length) {
          const i = agd.customOverlays.indexOf(preset);
          agd.customOverlays.splice(i, 1);
        }
      } else {
        const currentField = preset.fields.find((f) => f.name === field.name);
        if (currentField) {
          currentField.value = field.value;
        } else {
          preset.fields.push(_.cloneDeep(field));
        }
      }
    } else if (!shouldIgnore) {
      const preset = {
        id: co.id,
        layers: co.layers,
        fields: [_.cloneDeep(field)],
      };
      agd.customOverlays.push(preset);
    }
    if (!agd.customOverlays.length) {
      delete agd.customOverlays;
    }
  },

  async selectOverlayAssetLayout({ state, getters }, { layout, oldLayout }) {
    const clone = _.cloneDeepAndClean(layout);
    if (oldLayout) {
      clone.switchMainAndOverlay = oldLayout.switchMainAndOverlay;
      clone.assetBorder = oldLayout.assetBorder;
      clone.inAnimationId = oldLayout.inAnimationId;
      clone.outAnimationId = oldLayout.outAnimationId;
    }
    const sceneLayoutObject = app.getSceneLayoutObject();
    sceneLayoutObject[app.getOverlayLayoutAssetType(getters.workModel.type)] =
      clone;
    app.setSceneLayoutObject(sceneLayoutObject);
    if (
      !app.assetType.isWALLPAPER(getters.workModel) &&
      getters.selectedOverlayAsset.sceneAgd
    ) {
      const originalRatio = await app.getWidthHeight(
        getters.workModel.metadata
      );
      const innerRatio = {
        width:
          app.ASSET_EDITOR_WIDTH *
          (layout.mainAssetBoundingBox.right -
            layout.mainAssetBoundingBox.left),
        height:
          app.ASSET_EDITOR_HEIGHT *
          (layout.mainAssetBoundingBox.bottom -
            layout.mainAssetBoundingBox.top),
      };
      const agd = getters.selectedOverlayAsset.sceneAgd;
      getters.selectedOverlayAsset.sceneAgd = RCFH.AdjustAsset({
        originalRatio,
        innerRatio,
        agd,
      });
    }
    if (
      !app.assetType.isWALLPAPER(getters.selectedOverlayAsset) &&
      getters.selectedOverlayAsset.agd
    ) {
      const originalRatio = await app.getWidthHeight(
        getters.selectedOverlayAsset.metadata
      );
      const innerRatio = {
        width:
          app.ASSET_EDITOR_WIDTH *
          (layout.overlayAssetBoundingBox.right -
            layout.overlayAssetBoundingBox.left),
        height:
          app.ASSET_EDITOR_HEIGHT *
          (layout.overlayAssetBoundingBox.bottom -
            layout.overlayAssetBoundingBox.top),
      };
      const agd = getters.selectedOverlayAsset.agd;
      getters.selectedOverlayAsset.agd = RCFH.AdjustAsset({
        originalRatio,
        innerRatio,
        agd,
      });
    }
    // the setting of the sceneLayout **MUST** be in the end of the function because there
    // is a watch in the innerState that watch for changes only for the scene layout...
    getters.selectedOverlayAsset.sceneLayout = clone;
  },

  setCropData({ state, getters }, { main, overlay }) {
    if (!getters.selectedOverlayAsset.sceneAgd) {
      getters.selectedOverlayAsset.sceneAgd = {};
    }
    if (!getters.selectedOverlayAsset.agd) {
      getters.selectedOverlayAsset.agd = {};
    }
    getters.selectedOverlayAsset.sceneAgd.scale = main.scale;
    getters.selectedOverlayAsset.sceneAgd.offset = main.offset;
    getters.selectedOverlayAsset.agd.scale = overlay.scale;
    getters.selectedOverlayAsset.agd.offset = overlay.offset;
  },

  swapMediaOnOverlayAsset({ state, getters }) {
    const layout = getters.selectedLayout;
    Vue.set(layout, 'switchMainAndOverlay', !layout.switchMainAndOverlay);
  },

  setBorder({ state, getters }, borderPreset) {
    const layout = getters.selectedLayout;
    layout.assetBorder = borderPreset;
  },

  setTransitions({ state, getters }, { inAnimation, outAnimation }) {
    const layout = getters.selectedLayout;
    layout.inAnimationId = inAnimation;
    layout.outAnimationId = outAnimation;
  },
  generateSTPByWorkModel: _.debounce(
    ({ commit, getters, state }) => {
      const currentPage = getters.currentPage;
      if (
        ~_.indexOf(currentPage.groupBy, 'EFFECT') ||
        ~_.indexOf(state.stpPageIdsToIgnore, currentPage.id)
      ) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        const visualTrack = _.cloneDeep(state.workModel);
        visualTrack.agd = currentPage.agdAttributes.reduce((obj, attribute) => {
          const [key, id] = attribute.split('.'); // attribute is of the form 'customOverlays.Title'
          if (!_.isUndefined(id)) {
            const value = _.find(state.tmpAGD[key], { id });
            if (!value) {
              return { ...obj, [key]: state.tmpAGD[key] };
            }
            const customOverlayValue = _.cloneDeep(value);
            customOverlayValue.fields = value.fields.map((field) => {
              /**
               * when there is an area filed that contain z-index
               * remove the z-index and pass the value to the request of the STP
               * without the z-index.
               */
              if (app.regexs.areaWithZIndex.test(field.value)) {
                field.value = field.value.split(' ').slice(0, 4).join(' ');
              }
              return field;
            });
            if (customOverlayValue.fields.length === 0) {
              return obj;
            }
            return {
              ...obj,
              [key]: obj[key]
                ? obj[key].concat(customOverlayValue)
                : [customOverlayValue],
            };
          } else {
            return { ...obj, [key]: state.tmpAGD[key] };
          }
        }, {});
        if (_.isEmpty(visualTrack.agd)) {
          return resolve();
        }
        const stpPromise = app.realTimePreviewController.addTrackToPreviewQueue(
          {
            delay: 0,
            visualTrack,
            noInstantPreviewFieldsObject: getters.noInstantPreviewFieldsObject,
            silent: true,
            type: app.RealtimePreviewType.STATIC_MASK,
            source: 'assetEditor',
            forceStoryAspectRatio: true,
            height: 774,
          }
        );

        const updateSTP = function (stpModel) {
          if (
            state.stp.lastChecksum !== stpModel.get('realTimePreviewChecksum')
          ) {
            // old model, ignore.
            return;
          }

          if (stpModel.isWorking()) {
            commit('assignSTP', {
              status: 'PENDING',
            });
          } else {
            if (stpModel.isDone()) {
              commit('assignSTP', {
                status: 'DONE',
                src: app.prependHttp(
                  stpModel.attributes.realTimePreviewResultData.cdnUrl
                ),
              });
            } else {
              commit('assignSTP', {
                status: 'FAILED',
              });
            }
          }
        };

        stpPromise
          .then((stpModel) => {
            const previousChecksum = state.stp.lastChecksum;
            commit('assignSTP', {
              lastChecksum: stpModel.get('realTimePreviewChecksum'),
            });
            if (previousChecksum) {
              app.realTimePreviewController.abortRTP(
                previousChecksum,
                app.RealtimePreviewType.STATIC_MASK
              );
            }
            updateSTP(stpModel);
            stpModel.on('change:status', () => {
              updateSTP(stpModel);
            });
            resolve();
          })
          .fail(() => {
            commit('assignSTP', {
              status: null,
              src: null,
              lastChecksum: null,
            });
            resolve({ message: 'shouldPullPreview failed, no need for STP' });
          });
      });
    },
    450,
    {
      leading: false,
      trailing: true,
    }
  ),
  ADD_OR_UPDATE_CUSTOM_OVERLAY_FIELD({ commit, dispatch }, payload) {
    commit('addOrUpdateCustomOverlayField', payload);
    if (!payload.fieldDataModel) {
      dispatch('generateSTPByWorkModel');
    }
  },
  /**
   * Creates an object tracking job for the current custom overlay, when it has
   * AREA fields flagged with `trackArea`.
   *
   * A job is created only when something the tracker cares about changed since
   * the overlay page was opened - a tracked box moved, or the segment was
   * re-trimmed. Editing any other field (text, colour...) reuses the existing
   * id. Resolves with the id in use, rejects when the request fails so the
   * caller can keep the user on the page.
   *
   * @return {Promise<String|undefined>}
   */
  ENSURE_OBJECT_TRACKING({ state, commit, getters }) {
    const workModel = state.workModel;
    const page = getters.currentPage;

    if (!isTrackableAsset(workModel)) {
      return Promise.resolve();
    }

    const trackedFields = getTrackedAreaFields(
      getPresetFields(page, getters.presets)
    );

    if (_.isEmpty(trackedFields)) {
      return Promise.resolve();
    }

    const overlayId = page.id;
    const tmpOverlay = _.find(state.tmpAGD.customOverlays || [], {
      id: overlayId,
    });
    const appliedOverlay = _.find(
      (workModel.agd && workModel.agd.customOverlays) || [],
      { id: overlayId }
    );

    const coFields = (tmpOverlay && tmpOverlay.fields) || [];
    const appliedFields = (appliedOverlay && appliedOverlay.fields) || [];
    const existingId = appliedOverlay && appliedOverlay.objectTrackingId;

    // workModel.agd is frozen while the overlay page is open (changeCurrentPage
    // re-clones tmpAGD on entry), so this diff is "changed since it was opened"
    const boxesUnchanged = _.every(trackedFields, (field) =>
      isSameTrackedArea(field, coFields, appliedFields)
    );

    // a missing baseline means we cannot prove the segment is unchanged, so
    // re-track rather than risk keeping an id that describes another segment
    const baseline = state.pageTmpState.trackingBaseline;
    const segment = getSegmentTimes(workModel);
    const trimUnchanged =
      !!baseline &&
      baseline.startTime === segment.startTime &&
      baseline.endTime === segment.endTime;

    // checked before the reuse branch below: an overlay can arrive already
    // hidden - a preset gaining `defaultActive: false`, or an id stored before
    // the toggle existed - and reusing the id there would hand the renderer
    // tracking data for boxes it will not draw
    if (_.isEmpty(getActiveTrackedFields(trackedFields, coFields))) {
      if (existingId) {
        commit('setCustomOverlayObjectTrackingId', {
          id: overlayId,
          objectTrackingId: null,
        });
      }
      return Promise.resolve();
    }

    if (existingId && boxesUnchanged && trimUnchanged) {
      commit('setCustomOverlayObjectTrackingId', {
        id: overlayId,
        objectTrackingId: existingId,
      });
      return Promise.resolve(existingId);
    }

    // boxes are stored against the composed canvas; the tracker needs them
    // against the video, which takes the stage geometry to work out
    const assetRect = getTrackedAssetRect(workModel, state.stageDimensions);

    // both refusals below clear the id: the overlay carries whatever was applied
    // last, and leaving it would have the renderer apply the previous job's
    // tracking to a box that has since moved
    if (!assetRect) {
      app.doctorController.report(
        `ENSURE_OBJECT_TRACKING: cannot place the video for overlay ${overlayId} - unknown size, or a scene layout`
      );
      commit('setCustomOverlayObjectTrackingId', {
        id: overlayId,
        objectTrackingId: null,
      });
      return Promise.resolve();
    }

    const payload = buildTrackingPayload({
      trackedFields,
      coFields,
      workModel,
      timeInAsset: state.timeInAsset,
      stageDimensions: state.stageDimensions,
      assetRect,
    });

    if (!payload) {
      // a preset asks for tracking but none of its boxes could be parsed -
      // let the user apply, but make the misconfiguration visible
      app.doctorController.report(
        `ENSURE_OBJECT_TRACKING: no usable box for overlay ${overlayId}`
      );
      commit('setCustomOverlayObjectTrackingId', {
        id: overlayId,
        objectTrackingId: null,
      });
      return Promise.resolve();
    }

    commit('setObjectTrackingInFlight', true);

    return app.api.objectTracking
      .post(state.story.id, workModel.metadata.assetUUID, payload)
      .then((objectTrackingId) => {
        commit('setCustomOverlayObjectTrackingId', {
          id: overlayId,
          objectTrackingId,
        });
        commit('setObjectTrackingInFlight', false);
        // the render triggered by storing this id cannot contain the tracking
        // data yet - wait for the job and render the asset again when it lands.
        // the guid is passed because the id itself does not reach the timeline
        // until this edit is applied
        app.objectTrackingController.watch(objectTrackingId, workModel.guid);
        return objectTrackingId;
      })
      .catch((e) => {
        commit('setObjectTrackingInFlight', false);
        return Promise.reject(e);
      });
  },

  openOverlayAssetPage({ state, commit, dispatch }, { guid, standalone }) {
    commit('setTempTracks');
    commit('setActiveOverlayTrack', guid);
    state.overlayTrackStandAlone = standalone;
    dispatch('changeCurrentPage', 'overlay_asset');
    Analytics.trigger('analytics.scenes.openLayoutPage', {
      from: standalone ? 'timeline' : 'main',
    });
  },

  fireAnalyticsEvent({ state }, { name, data }) {
    let prefix;
    prefix = state.analyticsData.eventPrefix;
    prefix = prefix || 'unknown';
    const params = _.extend({}, state.analyticsData, data);
    delete params.eventPrefix;
    app.analytics(prefix + name + '', params);
  },

  APPLY_ASSET_CHANGES({ state, commit, dispatch, getters }) {
    commit('persistWorkModel');

    // apply volume to all other assets? keep AFTER model attributes set
    if (state.applyVolumeToAll) {
      app.timelineGateway.applyVolumeToAll(state.assetModel.attributes);
    }

    storyViewBus.$emit(
      'svbTimelineAfterAssetEdit',
      state.assetModel.attributes,
      {
        changed: true,
        durationChange: getters.isDurationChanged
          ? {
              oldDuration: state.oldDuration,
              newDuration: state.workModel.displayDuration,
            }
          : null,
      }
    );

    dispatch('fireAnalyticsEvent', {
      name: 'savedEdits',
      data: {
        Storyboard: state.isStoryboard,
        Super: !!getters.AGD.superTitle || !!getters.AGD.superName,
        Credit: !!getters.AGD.creditText,
        ZoomIN: getters.AGD.effectType === 'ZOOM_IN',
        ZoomOut: getters.AGD.effectType === 'ZOOM_OUT',
        'Text Overlays': getters.AGD.textOverlays?.length ?? 0,
        Subtitles: !!getters.AGD.videoQuoteSubTitles,
        'Zoom Crop': !!getters.AGD.scale && getters.AGD.scale > 1,
        'Positioning Changed': _.isObject(getters.AGD.offset),
        'Custom Overlay': _.isObject(getters.AGD.customOverlays),
      },
    });
  },

  NAVIGATE_TO_ASSET(
    { state, commit, dispatch, getters },
    { asset, suppressIan }
  ) {
    if (state.openedFrom === 'timeline') {
      // prior to navigation to the next `asset` serialization is needed
      commit('resetIanInstructions');

      if (state.currentPageId !== 'preview') {
        // we not on front-page page - preliminary asset effect serialization needed

        commit('buildIanInstructions', asset);

        // apply effect, return to front page
        bus.$emit('apply');
        dispatch('changeCurrentPage', 'preview');
      }

      if (getters.isDirty) {
        dispatch('APPLY_ASSET_CHANGES');
      }
    }

    if (suppressIan) {
      state.ian.total = 0;
    } else {
      commit('setIanContext', { asset });
    }

    commit('setCurrentAsset', { asset });
    checkIfPreviewNeeded(state);

    // NOTE: workaround for a non-responsive views - triggers destroy/create to reset their data
    state.ian.isAssetTuned = false;
    Vue.nextTick(() => {
      state.ian.isAssetTuned = true;

      dispatch('APPLY_NAVIGATION_INSTRUCTIONS');
    });
  },

  APPLY_NAVIGATION_INSTRUCTIONS({ state, dispatch }) {
    if (state.ian.instructions.editTOEffect) {
      dispatch('changeCurrentPage', 'textOverlay');

      if (state.ian.instructions.useTOModel) {
        state.ianToModel = Object.assign(state.ian.instructions.useTOModel, {
          overlay_id: $.now(),
          html: '',
          mode: 'editing',
        });
      }
    }
  },

  SET_TEXT_IMPORT_OPTIONS({ state, commit, rootState }) {
    const candidateForTranscribe = isAssetCandidateForTranscribe(
      state.workModel
    );

    const relevantAudioTracks = rootState.timemachine.trackLayer.audio.filter(
      (track) => {
        return (
          (track.data.displayStartTime <= state.workModel.displayStartTime &&
            track.data.displayEndTime >= state.workModel.displayEndTime) ||
          (track.data.displayStartTime >= state.workModel.displayStartTime &&
            track.data.displayStartTime <= state.workModel.displayEndTime) ||
          (track.data.displayEndTime >= state.workModel.displayStartTime &&
            track.data.displayEndTime <= state.workModel.displayEndTime)
        );
      }
    );

    const showDescription =
      !app.assetType.isGIF(state.workModel) &&
      !app.assetType.isWEBPAGE(state.workModel) &&
      !app.assetType.isWALLPAPER(state.workModel) &&
      state.workModel.metadata?.caption;
    let options = [];
    const isVideo =
      app.assetType.isVIDEO(state.workModel) ||
      app.assetType.isFRAGMENT(state.workModel);
    const assetTitle = state.workModel.metadata?.title;
    const assetUUID = state.workModel.metadata?.assetUUID;
    const assetDescription = state.workModel.metadata?.caption;
    const creationDate = state.workModel.metadata?.creationDate;
    const originalTranscript = state.workModel.metadata?.transcript;
    const videoDescription = app.storyController.story.get('description');

    // ...mapState('timemachine', {
    //   audioTracksLayer: (state) => state.trackLayer.audio,
    // }),
    options.push({
      key: 'assetDescription',
      label: isVideo ? 'Video Description' : 'Image Description',
      texts: [
        assetTitle,
        creationDate
          ? `${app
              .moment(creationDate)
              .format('MM/DD/YYYY hh:mma')} | ID: ${assetUUID}`
          : `ID: ${assetUUID}`,
        showDescription ? assetDescription : null,
      ],
    });

    if (candidateForTranscribe) {
      const transcriptions = [
        {
          id: state.workModel.metadata?.assetUUID,
          relevantTimes: [state.workModel.startTime, state.workModel.endTime],
          type: 'videoTranscript',
        },
      ];
      options.push({
        key: 'transcript',
        label: 'Transcript',
        transcriptionType: 'videoTranscript',
        transcriptions,
        texts: [],
      });
    }

    if (isVideo) {
      if (originalTranscript?.length) {
        options.push({
          key: 'originalTranscript',
          label: 'Original Transcript',
          texts: [originalTranscript],
        });
      }
    }

    if (videoDescription?.length) {
      options.push({
        key: 'storyInfo',
        label: 'Story Info',
        texts: [videoDescription],
      });
    }

    if (relevantAudioTracks.length) {
      const transcriptions = relevantAudioTracks.map((track) => {
        const startTimeShift = Math.max(
          0,
          state.workModel.displayStartTime - track.data?.displayStartTime
        );
        const endTimeShift = Math.max(
          0,
          track.data?.displayEndTime - state.workModel.displayEndTime
        );
        const startTime = track.data?.relativeStartTime + startTimeShift;
        const endTime = track.data?.relativeEndTime - endTimeShift;
        return {
          id: track.data?.id,
          title: track.data?.title,
          relevantTimes: [startTime, endTime],
          type: 'voiceOver',
        };
      });
      options.push({
        key: 'voiceOver',
        label: 'Voice-over',
        transcriptionType: 'voiceOver',
        transcriptions,
        texts: [],
      });
    }

    options = options.map((option) => {
      if (!option.texts) {
        option.texts = [];
      }
      option.texts = option.texts.filter((text) => text);
      return option;
    });
    options = options.filter((option) => {
      return option.texts.length || option.transcriptionType;
    });

    commit('setTextImportOptions', options);
  },
};
